/**
 * Filter utilities for managing filter parameters in localStorage
 * Similar to useLocalStorageParams hook pattern
 */

/**
 * Clear filter parameters from localStorage
 * @param {string} storageKey - The localStorage key to clear
 */
export const clearParamsFromStorage = storageKey => {
  try {
    localStorage.removeItem(storageKey);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to clear filter params from localStorage:', error);
  }
};

/**
 * Validate filter parameters against user permissions and table configuration
 * @param {object} params - Filter parameters to validate
 * @param {object} tableConfig - Table configuration with fields, filterFields, searchFields
 * @param {object} userContext - User context with isUserSuperAdmin and subscriptionAccess
 * @returns {object} Validated parameters or null if validation fails
 */
export const validateFilterParams = (params, tableConfig, userContext) => {
  if (!params || !tableConfig || !userContext) {
    return null;
  }

  const { fields = [], filterFields = [], searchFields = [] } = tableConfig;
  const { isUserSuperAdmin, subscriptionAccess } = userContext;

  // Validate sort field
  if (params.sort && params.sort.field) {
    const sortField = fields.find(field => (field.orderField || field.id) === params.sort.field);
    if (!sortField) {
      return null; // Invalid sort field
    }

    // Check if field is accessible based on show function
    if (sortField.show && typeof sortField.show === 'function') {
      if (!sortField.show({ isUserSuperAdmin })) {
        return null; // Field not accessible
      }
    }
  }

  // Validate search fields
  if (params.search && params.search.fields && Array.isArray(params.search.fields)) {
    if (tableConfig.searchFieldsAvailable && typeof tableConfig.searchFieldsAvailable === 'function') {
      if (!tableConfig.searchFieldsAvailable({ isUserSuperAdmin })) {
        return null;
      }
    }
    const invalidSearchFields = params.search.fields.filter(searchField => !searchFields.includes(searchField));
    if (invalidSearchFields.length > 0) {
      return null; // Invalid search fields
    }
  }

  // Validate filter fields
  if (params.filter && typeof params.filter === 'object') {
    const hasInvalidFilter = Object.entries(params.filter).some(([filterKey]) => {
      if (filterKey.includes('_min')) {
        filterKey = filterKey.replace('_min', '');
      } else if (filterKey.includes('_max')) {
        filterKey = filterKey.replace('_max', '');
      }
      const filterField = filterFields.find(field => field.id === filterKey);
      if (!filterField) {
        return true; // Invalid filter field
      }

      // Check show function
      if (filterField.show && typeof filterField.show === 'function') {
        if (!filterField.show({ isUserSuperAdmin })) {
          return true; // Field not accessible
        }
      }

      // Check isAvailable function
      if (filterField.isAvailable !== undefined) {
        let isAvailable = true;
        if (typeof filterField.isAvailable === 'function') {
          isAvailable = filterField.isAvailable({ isUserSuperAdmin, subscriptionAccess });
        } else {
          isAvailable = filterField.isAvailable;
        }
        if (!isAvailable) {
          return true; // Field not available
        }
      }

      return false; // Field is valid
    });

    if (hasInvalidFilter) {
      return null; // Invalid filter fields found
    }
  }

  return params; // All validations passed
};

/**
 * Get initial filter parameters from localStorage or return defaults
 * @param {string} storageKey - The localStorage key to use
 * @param {object} defaultParams - Default parameters if nothing is stored
 * @param {object} tableConfig - Table configuration for validation
 * @param {object} userContext - User context for validation
 * @param {object} urlParams - URL parameters (optional)
 * @returns {object} Filter parameters
 */
export const getInitialParams = (storageKey, defaultParams, tableConfig, userContext, urlParams = {}) => {
  // If URL params exist, they take priority
  if (tableConfig && Object.keys(urlParams).length > 0) {
    const validatedUrlParams = validateFilterParams(urlParams, tableConfig, userContext);

    if (validatedUrlParams) {
      return { params: validatedUrlParams };
    }
    // If URL params are invalid, fall back to localStorage or defaults
  }

  // Otherwise, try localStorage
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const parsed = JSON.parse(stored);
      const mergedParams = {
        ...defaultParams,
        ...parsed,
        ...(tableConfig ? {} : urlParams),
      };

      if (tableConfig) {
        // Validate stored params
        const validatedParams = validateFilterParams(mergedParams.params, tableConfig, userContext);
        if (validatedParams) {
          return mergedParams;
        }
        // If stored params are invalid, clear them and return defaults
        clearParamsFromStorage(storageKey);
      }
      return mergedParams;
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to parse stored filter params:', error);
  }

  return defaultParams;
};

/**
 * Save filter parameters to localStorage
 * @param {string} storageKey - The localStorage key to use
 * @param {object} params - Parameters to save
 */
export const saveParamsToStorage = (storageKey, params) => {
  try {
    localStorage.setItem(storageKey, JSON.stringify(params));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to save filter params to localStorage:', error);
  }
};

export default {
  getInitialParams,
  saveParamsToStorage,
  clearParamsFromStorage,
  validateFilterParams,
};
